import {
  DEFAULT_GIFT_CARD_AMOUNTS,
  RestaurantSettingKey,
  type CartItem,
  type CheckoutData,
  type Product,
  type RestaurantSetting,
  type TableCredit,
  type TableStatus,
} from "@lemmonhq/types";
import { createCartItem } from "@lemmonhq/orders";
import { nanoid } from "nanoid";

// Table credit: prepaid credit sold to a table for the night, stored as a gift
// card with kind = 'table_credit'. See docs/plans/table_credit.

/** Balance below which the POS warns that the table will need a top-up. */
export const TABLE_CREDIT_LOW_BALANCE = 100;

export const isTableCreditProduct = (product: Product) =>
  !!product.is_gift_card && product.gift_card_kind === "table_credit";

/** A product with price 0 lets the cashier type the amount. */
export const isOpenPriceCredit = (product: Product) =>
  !(parseFloat(product.price) > 0);

/**
 * Quick amounts offered when a gift card or table credit is rung up: the
 * restaurant's `gift_card_amounts` setting (Settings → Table orders), else
 * the defaults.
 */
export const giftCardAmountsOf = (
  settings: RestaurantSetting[] | undefined | null,
): number[] => {
  const raw = settings?.find(
    (s) => s.key === RestaurantSettingKey.GIFT_CARD_AMOUNTS,
  )?.value;
  if (!raw) return DEFAULT_GIFT_CARD_AMOUNTS;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    const amounts = Array.isArray(parsed)
      ? parsed.map(Number).filter((n) => Number.isFinite(n) && n > 0)
      : [];
    return amounts.length > 0
      ? [...new Set(amounts)].sort((a, b) => a - b)
      : DEFAULT_GIFT_CARD_AMOUNTS;
  } catch {
    return DEFAULT_GIFT_CARD_AMOUNTS;
  }
};

/** Cart lines that sell a plain gift card, given the loaded catalog. Like
 * table credit, a gift card is sold with "Pay", never sent to a table. */
export const giftCardItemsOf = (
  items: CartItem[],
  products: Product[],
): CartItem[] => {
  const ids = new Set(
    products
      .filter((p) => !!p.is_gift_card && !isTableCreditProduct(p))
      .map((p) => p.id),
  );
  return items.filter((item) => ids.has(item.productId) && item.quantity > 0);
};

/** Cart lines that sell table credit, given the loaded catalog. */
export const tableCreditItemsOf = (
  items: CartItem[],
  products: Product[],
): CartItem[] => {
  const ids = new Set(products.filter(isTableCreditProduct).map((p) => p.id));
  return items.filter((item) => ids.has(item.productId) && item.quantity > 0);
};

/** The credit that governs a table, or null when none was sold to it. */
export const tableCreditOf = (
  table: TableStatus | undefined | null,
): TableCredit | null => {
  const credit = table?.credit;
  if (!credit) return null;
  return {
    ...credit,
    balance: Number(credit.balance) || 0,
    held: Number(credit.held) || 0,
    owed: Number(credit.owed) || 0,
    loaded: Number(credit.loaded) || 0,
  };
};

/**
 * The amounts the table views show for a table: its order total and, when it
 * has credit, the credit loaded tonight and what is left of it after the
 * orders (loaded − total; negative once the orders go beyond the credit).
 */
export const tableAmountSummary = (
  table: TableStatus | undefined | null,
): { total: number; credit: { loaded: number; left: number } | null } => {
  const total = Number(table?.total_amount) || 0;
  const credit = tableCreditOf(table);
  if (!credit?.credited) return { total, credit: null };
  return {
    total,
    credit: { loaded: credit.loaded, left: credit.loaded - total },
  };
};

/**
 * What a table can still spend on new orders: its credit minus what its bill
 * already owes (the credit pays that at checkout).
 */
export const tableCreditAvailable = (credit: TableCredit | null): number =>
  credit ? Math.max(credit.balance - (credit.owed || 0), 0) : 0;

/**
 * Checkout payload selling `amount` of credit to a table. It is its own cart,
 * so the cart being built for the table is left untouched.
 */
export const tableCreditCheckout = (
  product: Product,
  tableId: number,
  amount: number,
): CheckoutData => {
  const item = createCartItem(product, {
    cartItemId: nanoid(),
    customPrice: amount,
    quantity: 1,
  });
  item.tableCreditTableId = tableId;
  return {
    payload: {
      type: "cart",
      object: { items: [item], customers: [], checkoutIds: [] },
    },
    serviceMethod: "dine-in",
    tableCreditTableId: tableId,
  };
};

export type TableCreditError =
  | { code: "insufficient"; balance: number; requested: number }
  | { code: "settlement_required"; balance: number }
  | { code: "settle_amount_changed"; balance: number }
  | { code: "payment_pending"; held: number }
  | { code: "product_on_table" };

/**
 * The SQL raises start with a stable code, e.g.
 * "table_credit_insufficient: balance=60.00 requested=200.00".
 */
export const parseTableCreditError = (
  message: string | undefined | null,
): TableCreditError | null => {
  if (!message) return null;
  const num = (name: string) => {
    const match = message.match(new RegExp(`${name}=(-?[0-9.]+)`));
    return match ? Number(match[1]) : 0;
  };
  if (message.includes("table_credit_insufficient"))
    return {
      code: "insufficient",
      balance: num("balance"),
      requested: num("requested"),
    };
  if (message.includes("table_credit_settlement_required"))
    return { code: "settlement_required", balance: num("balance") };
  if (message.includes("table_credit_settle_amount_changed"))
    return { code: "settle_amount_changed", balance: num("balance") };
  if (message.includes("table_credit_payment_pending"))
    return { code: "payment_pending", held: num("held") };
  if (message.includes("table_credit_product_on_table"))
    return { code: "product_on_table" };
  return null;
};
